import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSmartAppBanner } from '../hooks/useSmartAppBanner';

export default function PrivacyPage() {
  useSmartAppBanner();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900">
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap');
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .lowercase {
          text-transform: lowercase;
        }
        .safe-padding {
          padding-left: max(1rem, env(safe-area-inset-left));
          padding-right: max(1rem, env(safe-area-inset-right));
        }
      `}</style>

      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 safe-padding">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <CirclesIcon size={28} color="#cef7fc" isActive={true} />
              <span className="text-xl sm:text-2xl font-black lowercase text-neutral-900">circles</span>
            </Link>
            <Link
              to="/"
              className="bg-[#cef7fc] hover:bg-[#b8f0f7] text-neutral-900 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all active:scale-[0.98] shadow-sm hover:shadow-md"
            >
              Get the app
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 safe-padding">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black lowercase mb-8 text-neutral-900">privacy policy</h1>

        <p className="text-neutral-500 text-sm mb-8">Last updated: January 2025</p>

        <div className="prose prose-neutral max-w-none space-y-8">
          <Section title="1. introduction">
            <p>
              Welcome to Circles ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Service").
            </p>
            <p>
              By using Circles, you agree to the collection and use of information in accordance with this policy.
            </p>
          </Section>

          <Section title="2. information we collect">
            <h4 className="font-semibold text-neutral-800 mt-4">Information You Provide</h4>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>Account Information:</strong> When you create an account, we collect your phone number, display name, and any profile photos you upload.</li>
              <li><strong>Profile Information:</strong> You may provide additional information such as your age, interests, and bio.</li>
              <li><strong>Circle Information:</strong> Details about circles you create or join, including circle names, descriptions, and member information.</li>
              <li><strong>Communications:</strong> Messages, polls, and other content you send through the app.</li>
            </ul>

            <h4 className="font-semibold text-neutral-800 mt-4">Information Collected Automatically</h4>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>Location Data:</strong> With your permission, we collect your approximate location to show you nearby circles and enable location-based matching.</li>
              <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, and push notification tokens.</li>
              <li><strong>Usage Data:</strong> How you interact with the app, including features used, actions taken, and time spent.</li>
            </ul>
          </Section>

          <Section title="3. how we use your information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li>Provide, maintain, and improve our Service</li>
              <li>Match your circle with other compatible circles nearby</li>
              <li>Enable communication between matched circles</li>
              <li>Send you notifications about matches, messages, and app updates</li>
              <li>Ensure the safety and security of our platform</li>
              <li>Comply with legal obligations</li>
              <li>Analyze usage patterns to improve user experience</li>
            </ul>
          </Section>

          <Section title="4. information sharing">
            <p>We share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>With Other Users:</strong> Your profile information and circle membership are visible to other users as part of the matching and chat features.</li>
              <li><strong>Service Providers:</strong> We work with third-party services for hosting, analytics, and notifications (including Firebase/Google Cloud).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect rights, safety, or property.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, user information may be transferred.</li>
            </ul>
            <p className="mt-4">
              <strong>We do not sell your personal information to third parties.</strong>
            </p>
          </Section>

          <Section title="5. data retention">
            <p>
              We retain your information for as long as your account is active or as needed to provide you services. When you delete your account, we will delete your personal data within 30 days, except where we need to retain certain information for legal purposes or legitimate business interests.
            </p>
            <p>
              Messages and content in group chats may persist for other participants even after you leave or delete your account.
            </p>
          </Section>

          <Section title="6. data security">
            <p>
              We implement appropriate technical and organizational measures to protect your personal information, including encryption in transit and at rest, secure authentication, and regular security assessments.
            </p>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="7. your rights and choices">
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Delete your account and associated data</li>
              <li><strong>Opt-out:</strong> Disable push notifications in your device settings</li>
              <li><strong>Location:</strong> Revoke location permissions through your device settings</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at <a href="mailto:hello@getcircles.com" className="text-[#0ea5e9] hover:underline">hello@getcircles.com</a>.
            </p>
          </Section>

          <Section title="8. children's privacy">
            <p>
              Circles is not intended for users under 18 years of age. We do not knowingly collect personal information from children under 18. If we learn we have collected information from a child under 18, we will delete that information promptly.
            </p>
          </Section>

          <Section title="9. third-party services">
            <p>Our app uses the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-2 text-neutral-600">
              <li><strong>Firebase (Google):</strong> Authentication, database, storage, and analytics</li>
              <li><strong>Expo:</strong> Push notifications and app updates</li>
            </ul>
            <p className="mt-4">
              These services have their own privacy policies governing the use of your information.
            </p>
          </Section>

          <Section title="10. changes to this policy">
            <p>
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date. Continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. contact us">
            <p>
              If you have any questions about this privacy policy or our practices, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> <a href="mailto:hello@getcircles.com" className="text-[#0ea5e9] hover:underline">hello@getcircles.com</a>
            </p>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 sm:py-10 px-4 sm:px-6 safe-padding bg-white">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4 sm:gap-6">
          <Link to="/" className="text-xl font-black lowercase text-neutral-900">circles</Link>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-neutral-500">
            <Link to="/privacy" className="hover:text-gray-900 transition lowercase">privacy</Link>
            <Link to="/terms" className="hover:text-gray-900 transition lowercase">terms</Link>
            <a href="mailto:hello@getcircles.com" className="hover:text-gray-900 transition lowercase">support</a>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm">
            &copy; {new Date().getFullYear()} circles
          </p>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xl sm:text-2xl font-bold lowercase text-neutral-900 mb-4">{title}</h3>
      <div className="text-neutral-600 space-y-3 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function CirclesIcon({ size = 28, color = '#cef7fc', isActive = false }) {
  const outerStrokeWidth = 1.2;
  const innerStrokeWidth = 0.9;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'block', filter: 'drop-shadow(1px 1px 0px rgba(0, 0, 0, 0.9))' }}
    >
      {isActive ? (
        <>
          <path
            d="M14.92,16.72c2.69.92,6.1-1.5,6.12-4.41.19-2.8-2.16-5.14-4.97-5.16-.47-.01-.85.07-1,.28-.24.31.05,1,.23,1.53.79,2.38.47,5-.47,7.27-.02.13-.03.29.03.42l.06.07Z"
            fill={color}
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
          <path
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={innerStrokeWidth}
          />
          <circle
            cx="7.22"
            cy="11.89"
            r="6.03"
            fill={color}
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
        </>
      ) : (
        <>
          <path
            d="M14.68,16.7c.48.14.99.21,1.51.2,2.69-.07,4.82-2.32,4.74-5.01s-2.32-4.82-5.01-4.74c-.4.01-.79.07-1.16.17"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
          <path
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={innerStrokeWidth}
          />
          <circle
            cx="7.22"
            cy="11.89"
            r="6.03"
            fill="none"
            stroke={color}
            strokeLinecap="round"
            strokeMiterlimit="10"
            strokeWidth={outerStrokeWidth}
          />
        </>
      )}
    </svg>
  );
}